import { useTranslation } from 'react-i18next';
import { css, StyleSheet } from 'aphrodite';
import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useEffect, useMemo, useState } from 'react';
import { useParams, Params } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';

import { useGetCustomerById } from '@utils/api/back-office/customer-bo/use-get-customer-bo.tsx';
import { useUpdateCustomerBo } from '@utils/api/back-office/customer-bo/use-update-customer-bo.tsx';

import { CustomerBo } from '@utils/types/back-office/customer-bo.ts';

import Button from '@components/atoms/ui-kit/button.tsx';
import Tabs, { type Tab } from '@components/atoms/ui-kit/tabs.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import {
  Save,
  Building2,
  Settings,
  Palette,
  CreditCard,
  Wrench
} from 'lucide-react';
import { normalizeToE164Like } from '@components/atoms/ui-kit/phone-input.tsx';
import { getSafeColorForElement } from '@utils/helpers/color-utils';

import {
  buildValidationSchema,
  DEFAULT_BATCH_PRIORITY
} from './update-customer-form-schema';
import { CustomerOrgDetailsTab } from './tabs/customer-org-details-tab';
import { CustomerSettingsTab } from './tabs/customer-settings-tab';
import { CustomerBrandingTab } from './tabs/customer-branding-tab';
import { CustomerBillingTab } from './tabs/customer-billing-tab';
import { CustomerAdvancedTab } from './tabs/customer-advanced-tab';

export default function UpdateCustomersBoForm() {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();

  const { customerId } = useParams<Params>();
  const { data, isLoading } = useGetCustomerById(Number(customerId));

  const [currencyConflict, setCurrencyConflict] = useState(false);
  const [currencyConflictMessage, setCurrencyConflictMessage] = useState<
    string | null
  >(null);

  const { mutate: updateCustomer, isLoading: updatingCustomer } =
    useUpdateCustomerBo();

  const validationSchema = buildValidationSchema(t);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors }
  } = useForm<CustomerBo>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema as any),
    defaultValues: { batch_priority: DEFAULT_BATCH_PRIORITY }
  });

  useEffect(() => {
    if (data?.result) {
      const safe = data.result.brand_color
        ? getSafeColorForElement(data.result.brand_color, theme)
        : '';
      reset({
        ...data.result,
        name: data.result.name ?? '',
        email: data.result.email ?? '',
        phone: normalizeToE164Like(
          data.result.phone ?? '',
          data.result.address_country_code || 'NL'
        ),
        max_users: data.result.max_users ?? 3,
        is_active: data.result.is_active ?? false,
        is_reseller: data.result.is_reseller ?? false,
        receive_invoice_by_email: data.result.receive_invoice_by_email ?? false,
        use_data_consent: data.result.use_data_consent ?? false,
        diarization: data.result.diarization ?? false,
        use_plain_document: data.result.use_plain_document ?? false,
        default_integration_id: data.result.default_integration_id ?? 2,
        reseller_transcript_access: data.result.reseller_transcript_access ?? 0,
        brand_color: safe || '',
        billing_contact_firstname: data.result.billing_contact_firstname ?? '',
        billing_contact_lastname: data.result.billing_contact_lastname ?? '',
        billing_ref: data.result.billing_ref ?? '',
        billing_vat: data.result.billing_vat ?? '',
        address_street: data.result.address_street ?? '',
        address_postal_code: data.result.address_postal_code ?? '',
        address_city: data.result.address_city ?? '',
        address_country_code: data.result.address_country_code ?? '',
        microsoft_tid: data.result.microsoft_tid ?? '',
        mollie_id: data.result.mollie_id ?? '',
        jortt_id: data.result.jortt_id ?? '',
        wefact_id: data.result.wefact_id ?? '',
        logo: data.result.logo ?? '',
        batch_priority: data.result.batch_priority ?? DEFAULT_BATCH_PRIORITY,
        invoice_currency: data.result.invoice_currency ?? 'EUR',
        upload_suspended: data.result.upload_suspended ?? false
      });
    }
  }, [data, reset, theme]);

  const isValidPhone = (phone?: string | null): boolean =>
    !phone || /^(?:\+|00)?[1-9][0-9]{4,}$/.test(phone);

  const onSubmit = async (form: CustomerBo) => {
    if (!isValidPhone(form.phone)) {
      toast.error(t('please-enter-a-valid-phone-number'));
      return;
    }
    const payload = {
      ...form,
      phone: form.phone || null,
      microsoft_tid: form.microsoft_tid || null,
      mollie_id: form.mollie_id || null,
      jortt_id: form.jortt_id || null,
      wefact_id: form.wefact_id || null,
      brand_color: form.brand_color || null,
      batch_priority: form.batch_priority ?? DEFAULT_BATCH_PRIORITY
    };

    updateCustomer(
      { customerId: customerId || '', data: payload },
      {
        onSuccess: () => {
          toast.success(t('customer-updated-successfully'));
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error_description ||
            error?.response?.data?.detail?.[0]?.msg ||
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            t('an-error-occurred-while-updating-the-customer');
          if (error?.response?.status === 409) {
            setCurrencyConflict(true);
            setCurrencyConflictMessage(errorMessage);
            return;
          }
          setCurrencyConflict(false);
          setCurrencyConflictMessage(null);
          toast.error(errorMessage);
        }
      }
    );
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainerColor: { backgroundColor: getColour('appBackground') },
        mainText: { color: getColour('normalText') }
      }),
    [getColour]
  );

  const cardStyle = {
    backgroundColor: getColour('appSoft'),
    borderColor: getColour('hairline')
  };

  const tabProps = {
    register,
    control,
    errors,
    setValue,
    customer: data?.result,
    cardStyle
  };

  const tabsWithComponents: Tab[] = useMemo(
    () => [
      {
        id: 0,
        name: (
          <span className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {t('organization-details')}
          </span>
        ),
        component: <CustomerOrgDetailsTab {...tabProps} />
      },
      {
        id: 1,
        name: (
          <span className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            {t('settings')}
          </span>
        ),
        component: (
          <CustomerSettingsTab
            control={control}
            errors={errors}
            customer={data?.result}
            customerId={customerId}
            cardStyle={cardStyle}
          />
        )
      },
      {
        id: 2,
        name: (
          <span className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            {t('branding')}
          </span>
        ),
        component: (
          <CustomerBrandingTab
            control={control}
            errors={errors}
            setValue={setValue}
            customer={data?.result}
            customerId={customerId || ''}
            cardStyle={cardStyle}
          />
        )
      },
      {
        id: 3,
        name: (
          <span className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            {t('billing')}
          </span>
        ),
        component: (
          <CustomerBillingTab
            {...tabProps}
            currencyConflict={currencyConflict}
            setCurrencyConflict={setCurrencyConflict}
            currencyConflictMessage={currencyConflictMessage}
            setCurrencyConflictMessage={setCurrencyConflictMessage}
          />
        )
      },
      {
        id: 4,
        name: (
          <span className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            {t('advanced')}
          </span>
        ),
        component: (
          <CustomerAdvancedTab
            register={register}
            errors={errors}
            customer={data?.result}
            cardStyle={cardStyle}
          />
        )
      }
    ],
    [t, data, currencyConflict, currencyConflictMessage, errors]
  );

  return (
    <div>
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn(
            'flex flex-col overflow-y-auto pb-24 sm:pb-12 max-w-400 mx-auto px-2 sm:px-4',
            css(styles.mainContainerColor, styles.mainText)
          )}
        >
          <Tabs tabs={tabsWithComponents} minHeight="auto" maxHeight="none" />
          <div className="flex justify-center pt-6 pb-4 px-4">
            <Button
              type="submit"
              label={updatingCustomer ? t('updating') : t('update')}
              icon={<Save width={16} />}
              variant="primary"
              size="small"
              loading={updatingCustomer}
              disabled={updatingCustomer}
            />
          </div>
        </form>
      )}
    </div>
  );
}
